import React, { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, ArrowUpDown, Filter, ExternalLink, Zap, Eye, Wrench, MessageSquare, Image, Volume2, Database, ChevronDown, ChevronRight, Globe, Shield, Cloud, Lock } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const CAPABILITY_ICONS = {
  vision: Eye, tools: Wrench, streaming: Zap, json_mode: MessageSquare,
  function_calling: Wrench, image_generation: Image, audio: Volume2,
  embeddings: Database, parallel_tool_calls: Zap,
};

const TIER_CONFIG = {
  0: { label: "Tier 0", color: "bg-sky-500/10 text-sky-400 border-sky-500/20", icon: Zap, desc: "OpenAI-Compatible" },
  1: { label: "Tier 1", color: "bg-amber-500/10 text-amber-400 border-amber-500/20", icon: Wrench, desc: "Transform Adapter" },
  2: { label: "Tier 2", color: "bg-purple-500/10 text-purple-400 border-purple-500/20", icon: Cloud, desc: "Enterprise" },
};

function TierBadge({ tier }) {
  const cfg = TIER_CONFIG[tier] || TIER_CONFIG[0];
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] border ${cfg.color}`} data-testid={`tier-badge-${tier}`}>
      <Icon className="w-2.5 h-2.5" />{cfg.label}
    </span>
  );
}

function CapBadge({ cap }) {
  const Icon = CAPABILITY_ICONS[cap] || Zap;
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-[var(--qn-primary)]/10 text-[var(--qn-primary)] border border-[var(--qn-primary)]/20">
      <Icon className="w-2.5 h-2.5" />{cap.replace(/_/g, " ")}
    </span>
  );
}

function ProviderCard({ provider, onSelect, isSelected }) {
  return (
    <button
      onClick={() => onSelect(isSelected ? "" : provider.slug)}
      className={`text-left p-4 rounded-xl border transition-all duration-200 ${
        isSelected
          ? "border-[var(--qn-primary)] bg-[var(--qn-primary)]/[0.06]"
          : "border-[var(--qn-border)] bg-[var(--qn-card)] hover:border-[var(--qn-primary)]/40"
      }`}
      data-testid={`provider-card-${provider.slug}`}
      data-automation-id={`provider-card-${provider.slug}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Globe className="w-4 h-4 text-[var(--qn-primary)]" />
          <span className="font-medium text-sm text-[var(--qn-foreground)]">{provider.name}</span>
        </div>
        <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
          provider.status === "active" ? "bg-emerald-500/10 text-emerald-400" : "bg-amber-500/10 text-amber-400"
        }`}>{provider.status}</span>
      </div>
      <div className="flex items-center gap-2 text-xs text-[var(--qn-foreground-muted)] mb-1.5">
        <span>{provider.model_count} models</span>
        <span className="text-[var(--qn-border)]">|</span>
        <TierBadge tier={provider.tier} />
      </div>
      {provider.website && (
        <div className="flex items-center gap-1 text-[10px] text-[var(--qn-foreground-subtle)]">
          <ExternalLink className="w-2.5 h-2.5" />
          <span className="truncate">{provider.website.replace("https://", "")}</span>
        </div>
      )}
    </button>
  );
}

function ModelRow({ model, providers = [] }) {
  const provider = providers.find(p => p.slug === model.provider?.slug);
  const isVerified = provider?.pricing_verified !== false;
  const prefix = isVerified ? "$" : "~$";
  return (
    <tr className="border-b border-[var(--qn-border)]/50 hover:bg-white/[0.02] transition-colors">
      <td className="py-3 pr-4">
        <div className="text-sm font-medium text-[var(--qn-foreground)] truncate max-w-[240px]" title={model.id}>{model.display_name}</div>
        <div className="text-[10px] text-[var(--qn-foreground-subtle)] font-mono truncate max-w-[240px]">{model.id}</div>
      </td>
      <td className="py-3 pr-4">
        <span className="text-xs text-[var(--qn-foreground-muted)]">{model.provider.name}</span>
      </td>
      <td className="py-3 pr-4">
        <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
          model.type === "chat" ? "bg-blue-500/10 text-blue-400" :
          model.type === "embedding" ? "bg-purple-500/10 text-purple-400" :
          model.type === "image" ? "bg-pink-500/10 text-pink-400" :
          "bg-amber-500/10 text-amber-400"
        }`}>{model.type}</span>
      </td>
      <td className="py-3 pr-4 text-right" title={isVerified ? "Verified pricing" : "Estimated pricing — verify with provider"}>
        <span className="text-xs tabular-nums text-[var(--qn-foreground)]">
          {prefix}{model.pricing.input_per_million.toFixed(2)}
        </span>
        <span className="text-[10px] text-[var(--qn-foreground-subtle)]">/M</span>
      </td>
      <td className="py-3 pr-4 text-right" title={isVerified ? "Verified pricing" : "Estimated pricing — verify with provider"}>
        <span className="text-xs tabular-nums text-[var(--qn-foreground)]">
          {prefix}{model.pricing.output_per_million.toFixed(2)}
        </span>
        <span className="text-[10px] text-[var(--qn-foreground-subtle)]">/M</span>
      </td>
      <td className="py-3 pr-4 text-right">
        <span className="text-xs tabular-nums text-[var(--qn-foreground-muted)]">
          {model.context_window > 0 ? (model.context_window >= 1000000 ? `${(model.context_window / 1000000).toFixed(1)}M` : `${(model.context_window / 1000).toFixed(0)}K`) : "—"}
        </span>
      </td>
      <td className="py-3">
        <div className="flex flex-wrap gap-1">
          {Object.entries(model.capabilities).filter(([, v]) => v).slice(0, 3).map(([cap]) => (
            <CapBadge key={cap} cap={cap} />
          ))}
        </div>
      </td>
    </tr>
  );
}

export default function ProviderCatalog() {
  const [providers, setProviders] = useState([]);
  const [models, setModels] = useState([]);
  const [stats, setStats] = useState(null);
  const [search, setSearch] = useState("");
  const [selectedProvider, setSelectedProvider] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [capFilter, setCapFilter] = useState("");
  const [sort, setSort] = useState("provider");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/providers/stats`),
      axios.get(`${API}/providers/list`),
    ]).then(([statsRes, provRes]) => {
      setStats(statsRes.data);
      setProviders(provRes.data.providers);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    const params = new URLSearchParams();
    if (search) params.set("q", search);
    if (selectedProvider) params.set("provider", selectedProvider);
    if (typeFilter) params.set("type", typeFilter);
    if (capFilter) params.set("capability", capFilter);
    params.set("sort", sort);
    params.set("limit", "200");
    axios.get(`${API}/providers/models?${params}`).then(res => setModels(res.data.models));
  }, [search, selectedProvider, typeFilter, capFilter, sort]);

  if (loading) return <div className="text-center py-12 text-[var(--qn-foreground-muted)]">Loading catalog...</div>;

  return (
    <div className="space-y-6" data-testid="provider-catalog" data-automation-id="provider-catalog">
      {/* Stats bar */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4">
          {[
            { label: "Providers", value: stats.total_providers, color: "text-[var(--qn-primary)]" },
            { label: "Models", value: stats.total_models, color: "text-emerald-400" },
            { label: "Chat Models", value: stats.by_type.chat || 0, color: "text-blue-400" },
            { label: "Embedding", value: stats.by_type.embedding || 0, color: "text-purple-400" },
            { label: "With Vision", value: stats.by_capability.vision || 0, color: "text-amber-400" },
            { label: "With Tools", value: stats.by_capability.tools || 0, color: "text-pink-400" },
          ].map((s, i) => (
            <div key={i} className="px-4 py-3 rounded-xl bg-[var(--qn-card)] border border-[var(--qn-border)]" data-testid={`catalog-stat-${i}`}>
              <div className={`text-2xl font-bold tabular-nums ${s.color}`}>{s.value}</div>
              <div className="text-xs text-[var(--qn-foreground-muted)]">{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Provider cards */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium text-[var(--qn-foreground-muted)]">Providers</h3>
          <div className="flex items-center gap-2">
            {Object.entries(TIER_CONFIG).map(([t, cfg]) => (
              <span key={t} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] border ${cfg.color}`}>
                {cfg.label} — {cfg.desc}
              </span>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {providers.map(p => (
            <ProviderCard key={p.slug} provider={p} onSelect={setSelectedProvider} isSelected={selectedProvider === p.slug} />
          ))}
        </div>
      </div>

      {/* Search & filters */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--qn-foreground-subtle)]" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search models..."
            className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-[var(--qn-background)] border border-[var(--qn-border)] text-sm text-[var(--qn-foreground)] placeholder:text-[var(--qn-foreground-subtle)] focus:outline-none focus:ring-1 focus:ring-[var(--qn-primary)]"
            data-testid="provider-search"
            data-automation-id="provider-search"
          />
        </div>
        <select
          value={typeFilter}
          onChange={e => setTypeFilter(e.target.value)}
          className="px-3 py-2.5 rounded-lg bg-[var(--qn-background)] border border-[var(--qn-border)] text-sm text-[var(--qn-foreground)]"
          data-testid="provider-type-filter"
        >
          <option value="">All Types</option>
          <option value="chat">Chat</option>
          <option value="embedding">Embedding</option>
          <option value="image">Image</option>
          <option value="audio">Audio</option>
        </select>
        <select
          value={capFilter}
          onChange={e => setCapFilter(e.target.value)}
          className="px-3 py-2.5 rounded-lg bg-[var(--qn-background)] border border-[var(--qn-border)] text-sm text-[var(--qn-foreground)]"
          data-testid="provider-cap-filter"
        >
          <option value="">All Capabilities</option>
          <option value="vision">Vision</option>
          <option value="tools">Tools</option>
          <option value="streaming">Streaming</option>
          <option value="json_mode">JSON Mode</option>
          <option value="function_calling">Function Calling</option>
        </select>
        <select
          value={sort}
          onChange={e => setSort(e.target.value)}
          className="px-3 py-2.5 rounded-lg bg-[var(--qn-background)] border border-[var(--qn-border)] text-sm text-[var(--qn-foreground)]"
          data-testid="provider-sort"
        >
          <option value="provider">Sort: Provider</option>
          <option value="price_asc">Sort: Price (Low-High)</option>
          <option value="price_desc">Sort: Price (High-Low)</option>
          <option value="context">Sort: Context Window</option>
          <option value="name">Sort: Name</option>
        </select>
      </div>

      {/* Model table */}
      <div className="rounded-xl border border-[var(--qn-border)] bg-[var(--qn-card)] overflow-hidden">
        <div className="px-4 py-3 border-b border-[var(--qn-border)] flex items-center justify-between">
          <span className="text-sm font-medium text-[var(--qn-foreground)]">
            {models.length} Model{models.length !== 1 ? "s" : ""}
            {selectedProvider && ` from ${providers.find(p => p.slug === selectedProvider)?.name || selectedProvider}`}
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[var(--qn-border)] text-xs text-[var(--qn-foreground-muted)]">
                <th className="text-left py-2.5 px-4 font-medium">Model</th>
                <th className="text-left py-2.5 pr-4 font-medium">Provider</th>
                <th className="text-left py-2.5 pr-4 font-medium">Type</th>
                <th className="text-right py-2.5 pr-4 font-medium">Input $/M</th>
                <th className="text-right py-2.5 pr-4 font-medium">Output $/M</th>
                <th className="text-right py-2.5 pr-4 font-medium">Context</th>
                <th className="text-left py-2.5 pr-4 font-medium">Capabilities</th>
              </tr>
            </thead>
            <tbody>
              {models.map((m, i) => <ModelRow key={`${m.provider.slug}-${m.id}-${i}`} model={m} providers={providers} />)}
            </tbody>
          </table>
          {models.length === 0 && (
            <div className="text-center py-8 text-sm text-[var(--qn-foreground-muted)]">No models match your filters</div>
          )}
        </div>
      </div>
    </div>
  );
}
